/**
 * §4.1 damage pipeline, §4.10 player intake gates, §03.7 knockback, §03.10 hit-stop + trauma,
 * plus step 14 lifecycle (death animation, pool release, run outcome).
 *
 * Determinism: the only RNG consumed here is `w.rng.crit` (§4.11), one roll per hit event when
 * crit chance > 0. `w.rng.vfx` is touched only by `sampleTrauma`, which is presentation-only.
 */

import { STEP } from '../core/loop';
import { cfg } from '../core/config';
import { bossById, enemyById } from '../core/content';
import type { EventPayloads, HitContact } from '../core/events';
import type { Enemy, EntityBase, Player } from './entities';
import type { World } from './world';
import { dropSurgeTiers, getPlayerRuntime, surgeDamageMultiplier, applyKnockbackToPlayer } from './player';
import { statusArmorShred, statusDamageTakenBonus } from './status';
import { wrapDist } from './wrap';

// --- §03.10 feedback constants ---

/** Hit-stop frame classes. `light` (standard enemy hit) is 0 and never requested. */
export const HITSTOP_HEAVY = 2;
export const HITSTOP_ELITE_KILL = 3;
export const HITSTOP_PLAYER_HIT = 4;
export const HITSTOP_BOSS_EVENT = 6;

/** Screen-shake trauma adds (§03.10 / §11.7). */
export const TRAUMA_PLAYER_HIT = 0.5;
export const TRAUMA_BOSS_EVENT = 0.6;
export const TRAUMA_EXPLOSION = 0.3;
export const TRAUMA_EXPLOSION_RADIUS = 15;
export const TRAUMA_ELITE_KILL = 0.25;
export const TRAUMA_HEAVY_FIRE = 0.05;

/** §4.1 — crit multiplier baseline and caps, mitigation cap. */
export const CRIT_CHANCE_CAP = 0.75;
export const CRIT_MULT_BASE = 2.0;
export const CRIT_MULT_CAP = 4.0;
export const MITIGATION_CAP = 0.8;

/** §4.7 step 6 — proc-generated hits at depth ≥ 2 fire no further procs. */
export const MAX_PROC_DEPTH = 2;

/** Death animation length before the pool slot is released (§14.3). */
export const DEATH_ANIM_TICKS = 12;
/** Spawn/birth animation length once the spawn telegraph has elapsed (§8.5). */
export const SPAWN_ANIM_TICKS = 12;

/** §03.7 — enemy knockback decays at 30 u/s²; total speed clamped to 1.5× base. */
export const ENEMY_KNOCK_DECAY = 30;
export const ENEMY_SPEED_CLAMP = 1.5;

/** §7.5 affix.shielded: pool = 50% max HP, regen 10%/s after 3 s unhit, 300 ms break stagger. */
export const SHIELD_POOL_FRAC = 0.5;
export const SHIELD_REGEN_FRAC_PER_S = 0.1;
export const SHIELD_REGEN_DELAY_TICKS = 180;
export const SHIELD_BREAK_STAGGER_TICKS = 18;

/** §07 conventions — contact damage growth `dmg_base × 1.08^(t/120)`, capped ×2.0. */
export const CONTACT_GROWTH_BASE = 1.08;
export const CONTACT_GROWTH_CAP = 2.0;
/** Elite contact damage ×1.5 (§7.5). */
export const ELITE_CONTACT_MULT = 1.5;

// --- preallocated event payloads (zero allocation in the hot loop, §14.3) ---

const RING = 256;
const hitRing: EventPayloads['hit'][] = new Array(RING);
/**
 * Contact records live parallel to the hit ring, one per slot. A hit with a contact point
 * points at its own slot; a hit without one carries `null`. Both cost zero allocation, and
 * `null` cannot be mistaken for a position the way a (0, 0) sentinel can.
 */
const contactRing: HitContact[] = new Array(RING);
const deathRing: EventPayloads['death'][] = new Array(RING);
for (let i = 0; i < RING; i++) {
  hitRing[i] = { src_id: 0, tgt_id: 0, weapon_id: '', damage: 0, crit: false, killed: false, depth: 0, pos: { x: 0, y: 0 }, radius: 0, contact: null };
  contactRing[i] = { x: 0, y: 0, dir_x: 0, dir_y: 0 };
  deathRing[i] = { entity_id: 0, killer_id: 0, cause_id: '', pos: { x: 0, y: 0 }, elite: false, def_id: '', threat: 0 };
}
let hitCursor = 0;
let deathCursor = 0;

const playerHitPayload: EventPayloads['player_hit'] = {
  source_id: 0,
  amount: 0,
  hp_after: 0,
  telegraph_event_id: 0,
  source_def_id: '',
};
const gameOverPayload: EventPayloads['game_over'] = { outcome: 'defeat' };

/** §4.5 — round_half_up(x) = floor(x + 0.5), minimum 1. */
function roundDamage(x: number): number {
  const r = Math.floor(x + 0.5);
  return r < 1 ? 1 : r;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

// --- §03.10 hit-stop and trauma ---

/**
 * Requests `frames` of hit-stop. Global cap is 8 frames per rolling 1 s; an event that would
 * overflow the budget degrades to 0 frames entirely (§03.10), it is never partially applied.
 */
export function requestHitStop(w: World, frames: number): void {
  if (frames <= 0) return;
  const hs = w.feedback.hitstop;
  if (w.tick - hs.window_start_tick >= 60) {
    hs.window_start_tick = w.tick;
    hs.spent_in_window = 0;
  }
  const cap = cfg.get('feel.hitstop_cap_per_s');
  if (hs.spent_in_window + frames > cap) return;
  hs.spent_in_window += frames;
  if (frames > hs.frames_remaining) hs.frames_remaining = frames;
}

/** Sim-time dilation for the current frame; ×0.1 while hit-stop is active (§03.10). */
export function hitStopDilation(w: World): number {
  return w.feedback.hitstop.frames_remaining > 0 ? cfg.get('feel.hitstop_dilation') : 1;
}

/** Adds screen-shake trauma; total clamped to 1.0 (§03.10). */
export function addTrauma(w: World, amount: number): void {
  if (amount <= 0) return;
  const t = w.feedback.trauma + amount;
  w.feedback.trauma = t > 1 ? 1 : t;
}

/** §03.10 — explosion trauma 0.30 × (1 − d/15), nothing beyond 15 u. */
export function addExplosionTrauma(w: World, x: number, y: number): void {
  const d = wrapDist(x, y, w.player.pos.x, w.player.pos.y);
  if (d >= TRAUMA_EXPLOSION_RADIUS) return;
  addTrauma(w, TRAUMA_EXPLOSION * (1 - d / TRAUMA_EXPLOSION_RADIUS));
}

const shakeOut = { x: 0, y: 0, rot: 0 };

/**
 * PRESENTATION ONLY (§03.10, spine §7): samples the shake offset for one rendered frame.
 * Consumes `w.rng.vfx` exclusively and never touches sim state. The sim must not call this.
 */
export function sampleTrauma(w: World): { x: number; y: number; rot: number } {
  const t = w.feedback.trauma;
  const amp = t * t;
  const maxOffset = cfg.get('feel.shake_max_offset');
  const maxRot = (cfg.get('feel.shake_max_rot_deg') * Math.PI) / 180;
  shakeOut.x = (w.rng.vfx.next() * 2 - 1) * amp * maxOffset;
  shakeOut.y = (w.rng.vfx.next() * 2 - 1) * amp * maxOffset;
  shakeOut.rot = (w.rng.vfx.next() * 2 - 1) * amp * maxRot;
  return shakeOut;
}

// --- enemy stat helpers ---

/** Mass for knockback (§03.7): standard 0.0, elite 0.6, mini-boss 0.9, boss 1.0. */
export function enemyMass(e: Enemy): number {
  if (e.is_boss) {
    const def = bossById.get(e.def_id);
    const kind = def === undefined ? '' : String((def as unknown as Record<string, unknown>).kind ?? '');
    return kind === 'mini_boss' ? 0.9 : 1.0;
  }
  return e.elite ? 0.6 : 0.0;
}

/** Base movement speed from the content table, including the elite and `affix.swift` multipliers. */
export function enemyBaseSpeed(e: Enemy): number {
  const def = enemyById.get(e.def_id);
  let s = def === undefined ? 4 : def.stats.move_speed;
  if (e.elite) s *= 1.1;
  for (let i = 0; i < e.affix_count; i++) {
    if (e.affixes[i] === 'affix.swift') s *= 1.35;
  }
  return s;
}

/** Flat armor fraction from the content table; `status.corrode` shred is applied by the pipeline. */
function enemyArmor(e: Enemy): number {
  const def = enemyById.get(e.def_id);
  if (def === undefined) return 0;
  const v = (def.stats as unknown as Record<string, unknown>).armor_pct;
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/** §07 — contact damage at the enemy's spawn time, elite multiplier included. */
export function contactDamageOf(w: World, e: Enemy): number {
  const def = enemyById.get(e.def_id);
  let base = def === undefined ? 8 : def.stats.contact_damage_base;
  const t = e.spawn_tick / 60;
  let growth = Math.pow(CONTACT_GROWTH_BASE, t / 120);
  if (growth > CONTACT_GROWTH_CAP) growth = CONTACT_GROWTH_CAP;
  base *= growth;
  if (e.elite) base *= ELITE_CONTACT_MULT;
  return base;
}

// --- §4.1 damage pipeline ---

/**
 * §4.1 steps 3–6 for an enemy target. `amount` is the attacker-side damage already resolved by
 * the firing weapon (steps 1–2 plus the player's `more` multipliers, snapshotted at fire time
 * per A.4 `Projectile.damage_snapshot`). This function applies Lumensurge, the target's
 * damage-taken bonus, the crit roll, mitigation and rounding, then commits the hit.
 *
 * `contact` is where the blow landed, for the callers that have it (a projectile knows its
 * contact point; a status tick does not). It is presentation-only — nothing in this pipeline
 * reads it — and it is a required argument so a contact-less source says so in one word
 * instead of inheriting a default it never thought about.
 *
 * Returns the final integer damage dealt (0 when the target was not a legal victim).
 */
export function applyDamageToEnemy(
  w: World,
  enemy: Enemy,
  amount: number,
  weaponId: string,
  srcId: number,
  canCrit: boolean,
  depth: number,
  contact: HitContact | null
): number {
  if (!enemy.alive || enemy.death_anim > 0 || amount <= 0) return 0;

  let dmg = amount;
  // step 3 — independent multipliers
  dmg *= surgeDamageMultiplier(w);
  dmg *= 1 + statusDamageTakenBonus(enemy);

  // step 4 — crit; one roll per hit event, `crit` stream, only when chance > 0 (§4.3)
  let crit = false;
  if (canCrit) {
    const cc = Math.min(w.player.stats.crit_chance, CRIT_CHANCE_CAP);
    if (cc > 0 && w.rng.crit.next() < cc) {
      crit = true;
      dmg *= clamp(w.player.stats.crit_mult, CRIT_MULT_BASE, CRIT_MULT_CAP);
    }
  }

  // step 5 — armor / resist, shred subtracts before the clamp, armor floored at 0 (§4.4)
  const armor = Math.max(0, enemyArmor(enemy) - statusArmorShred(enemy));
  dmg *= 1 - clamp(armor, 0, MITIGATION_CAP);

  // step 6 — integer rounding, minimum 1
  const final = roundDamage(dmg);

  commitEnemyDamage(w, enemy, final, weaponId, srcId, crit, depth, contact);
  return final;
}

/** §4.9 — status ticks: no crit, no RNG, ignore armor/resist, raised by damage-taken bonuses. */
export function dealStatusDamage(
  w: World,
  target: EntityBase,
  raw: number,
  statusId: string,
  sourceId: number
): number {
  if (raw <= 0 || !target.alive) return 0;
  if (target.kind === 'player') {
    return applyPlayerIntake(w, raw, sourceId, statusId, true) ? Math.max(1, Math.floor(raw + 0.5)) : 0;
  }
  if (target.kind !== 'enemy') return 0;
  const e = target as Enemy;
  if (e.death_anim > 0) return 0;
  const final = roundDamage(raw * (1 + statusDamageTakenBonus(e)));
  // A burn tick happens everywhere on the body at once: there is no contact point.
  commitEnemyDamage(w, e, final, statusId, sourceId, false, 1, null);
  return final;
}

/** Shield absorption, HP application, hit event, hit-stop and death dispatch. */
function commitEnemyDamage(
  w: World,
  enemy: Enemy,
  final: number,
  weaponId: string,
  srcId: number,
  crit: boolean,
  depth: number,
  contact: HitContact | null
): void {
  let remaining = final;
  if (enemy.shield > 0) {
    const absorbed = enemy.shield < remaining ? enemy.shield : remaining;
    enemy.shield -= absorbed;
    remaining -= absorbed;
    if (enemy.shield <= 0) {
      enemy.shield = 0;
      enemy.stagger_until = w.tick + SHIELD_BREAK_STAGGER_TICKS;
    }
  }
  enemy.shield_regen_at = w.tick + SHIELD_REGEN_DELAY_TICKS;
  if (remaining > 0) enemy.hp -= remaining;
  enemy.hp_frac = enemy.max_hp > 0 ? Math.max(0, enemy.hp / enemy.max_hp) : 0;

  w.counters.damage_dealt += final;
  const byWeapon = w.counters.damage_by_weapon;
  const prev = byWeapon[weaponId];
  byWeapon[weaponId] = (prev === undefined ? 0 : prev) + final;

  const killed = enemy.hp <= 0;
  const slot = hitCursor;
  const ev = hitRing[slot];
  hitCursor = (hitCursor + 1) % RING;
  ev.src_id = srcId;
  ev.tgt_id = enemy.id;
  ev.weapon_id = weaponId;
  ev.damage = final;
  ev.crit = crit;
  ev.killed = killed;
  ev.depth = depth;
  ev.pos.x = enemy.pos.x;
  ev.pos.y = enemy.pos.y;
  // The impact beat is scaled to the body, so presentation needs the body's size. Carrying
  // it on the event costs one field; the alternative is the renderer scanning the enemy
  // pool for `tgt_id` on every hit.
  ev.radius = enemy.hitbox_radius;
  // Copied, not aliased: callers hand over a reused scratch record, and the bus delivers
  // the event a flush later.
  if (contact === null) {
    ev.contact = null;
  } else {
    const c = contactRing[slot];
    c.x = contact.x;
    c.y = contact.y;
    c.dir_x = contact.dir_x;
    c.dir_y = contact.dir_y;
    ev.contact = c;
  }
  w.bus.emit('hit', ev);

  // §03.10 — `heavy` class: crit, or an evolved-weapon kill.
  if (crit) requestHitStop(w, HITSTOP_HEAVY);

  if (killed) killEnemy(w, enemy, srcId, weaponId);
}

/** Marks an enemy dead: death animation starts, the pool slot is released by stepLifecycle. */
export function killEnemy(w: World, enemy: Enemy, killerId: number, causeId: string): void {
  if (!enemy.alive || enemy.death_anim > 0) return;
  enemy.hp = 0;
  enemy.hp_frac = 0;
  enemy.targetable = false;
  enemy.death_anim = 1 / DEATH_ANIM_TICKS;
  enemy.vel.x = 0;
  enemy.vel.y = 0;
  enemy.knock.x = 0;
  enemy.knock.y = 0;

  w.counters.kills++;
  if (enemy.is_boss) {
    w.counters.boss_kills++;
    requestHitStop(w, HITSTOP_BOSS_EVENT);
    addTrauma(w, TRAUMA_BOSS_EVENT);
  } else if (enemy.elite) {
    w.counters.elite_kills++;
    requestHitStop(w, HITSTOP_ELITE_KILL);
    addTrauma(w, TRAUMA_ELITE_KILL);
  }

  const ev = deathRing[deathCursor];
  deathCursor = (deathCursor + 1) % RING;
  ev.entity_id = enemy.id;
  ev.killer_id = killerId;
  ev.cause_id = causeId;
  ev.pos.x = enemy.pos.x;
  ev.pos.y = enemy.pos.y;
  ev.elite = enemy.elite;
  ev.def_id = enemy.def_id;
  ev.threat = enemy.threat_score;
  w.bus.emit('death', ev);
}

/**
 * §03.7 — weapon knockback on enemies: `impulse × (1 − mass)`, added to the enemy's separate
 * knock vector so steering is untouched. Total speed contribution is clamped so the enemy never
 * exceeds 1.5× its base speed.
 */
export function applyKnockbackToEnemy(w: World, enemy: Enemy, nx: number, ny: number, impulse: number): void {
  if (!enemy.alive || enemy.death_anim > 0 || impulse <= 0) return;
  const m = Math.sqrt(nx * nx + ny * ny);
  if (m < 1e-6) return;
  const eff = impulse * (1 - enemyMass(enemy)) * w.player.stats.knockback_mult;
  if (eff <= 0) return;
  enemy.knock.x += (nx / m) * eff;
  enemy.knock.y += (ny / m) * eff;
  const cap = (ENEMY_SPEED_CLAMP - 1) * enemyBaseSpeed(enemy);
  const km = Math.sqrt(enemy.knock.x * enemy.knock.x + enemy.knock.y * enemy.knock.y);
  if (km > cap && km > 1e-9) {
    enemy.knock.x = (enemy.knock.x / km) * cap;
    enemy.knock.y = (enemy.knock.y / km) * cap;
  }
}

// --- §4.10 player intake ---

/**
 * §4.10 gate order: 1 dash i-frames, 2 mercy window, 3 resolve_hit with the attacker's stats,
 * 4 on HP loss apply the Lumensurge penalty, mercy, hit-stop and trauma.
 * Returns true when HP was actually lost.
 */
export function applyDamageToPlayer(w: World, amount: number, sourceId: number, sourceDefId: string): boolean {
  return applyPlayerIntake(w, amount, sourceId, sourceDefId, false);
}

function applyPlayerIntake(
  w: World,
  amount: number,
  sourceId: number,
  sourceDefId: string,
  ignoreArmor: boolean
): boolean {
  const p: Player = w.player;
  if (!p.alive || w.outcome !== 'running' || amount <= 0) return false;
  if (cfg.getBool('bench.god_mode')) return false;
  // gate 1 — dash i-frames (§03.4): exactly 15 ticks from dash start, no Lumensurge penalty.
  if (w.tick < p.dash.iframes_until) return false;
  // gate 2 — mercy window (§03.7): all further intake blocked.
  if (w.tick < p.mercy_until) return false;

  let dmg = amount * (1 + statusDamageTakenBonus(p));
  if (!ignoreArmor) dmg *= 1 - clamp(p.stats.armor, 0, MITIGATION_CAP);
  const final = roundDamage(dmg);

  p.hp -= final;
  if (p.hp < 0) p.hp = 0;
  w.counters.damage_taken += final;
  p.mercy_until = w.tick + Math.round(cfg.get('player.mercy_s') * 60);

  dropSurgeTiers(w, cfg.get('surge.hit_penalty_tiers'), 'hit');
  requestHitStop(w, HITSTOP_PLAYER_HIT);
  addTrauma(w, TRAUMA_PLAYER_HIT);

  playerHitPayload.source_id = sourceId;
  playerHitPayload.amount = final;
  playerHitPayload.hp_after = p.hp;
  playerHitPayload.telegraph_event_id = 0;
  playerHitPayload.source_def_id = sourceDefId;
  w.bus.emit('player_hit', playerHitPayload);
  return true;
}

// --- step 14: lifecycle ---

/** Called by the director (R4) when the final boss dies (spine §4: victory ends the run). */
export function setVictory(w: World): void {
  if (w.outcome !== 'running') return;
  w.outcome = 'victory';
  gameOverPayload.outcome = 'victory';
  w.bus.emit('game_over', gameOverPayload);
}

export function stepLifecycle(w: World): void {
  // §03.10 — trauma decays at 1.2/s; hit-stop burns down one frame per sim tick.
  const decay = cfg.get('feel.shake_decay') * STEP;
  if (w.feedback.trauma > 0) {
    w.feedback.trauma -= decay;
    if (w.feedback.trauma < 0) w.feedback.trauma = 0;
  }
  if (w.feedback.hitstop.frames_remaining > 0) w.feedback.hitstop.frames_remaining--;

  const live = w.enemies.liveIndices();
  for (let i = 0; i < live.length; i++) {
    const e = w.enemies.slots[live[i]];
    if (!e.alive) continue;
    if (e.death_anim > 0) {
      e.death_anim += 1 / DEATH_ANIM_TICKS;
      if (e.death_anim >= 1) {
        e.death_anim = 0;
        e.status_count = 0;
        w.enemies.release(e);
      }
      continue;
    }
    // birth progress once the spawn telegraph has elapsed (§8.5 spawn safety)
    if (e.spawn_anim < 1) {
      if (w.tick >= e.telegraph_until) {
        e.spawn_anim += 1 / SPAWN_ANIM_TICKS;
        if (e.spawn_anim >= 1) {
          e.spawn_anim = 1;
          e.targetable = true;
        }
      }
      continue;
    }
    // §7.5 affix.shielded regeneration
    if (e.shield_regen_at > 0 && w.tick >= e.shield_regen_at && hasShieldAffix(e)) {
      const pool = e.max_hp * SHIELD_POOL_FRAC;
      if (e.shield < pool) {
        e.shield += pool * SHIELD_REGEN_FRAC_PER_S * STEP;
        if (e.shield > pool) e.shield = pool;
      }
    }
  }

  const p = w.player;
  if (p.alive && p.hp <= 0) {
    p.hp = 0;
    p.alive = false;
    p.targetable = false;
    p.vel.x = 0;
    p.vel.y = 0;
    getPlayerRuntime(w).knockX = 0;
    getPlayerRuntime(w).knockY = 0;
    w.outcome = 'defeat';
    gameOverPayload.outcome = 'defeat';
    w.bus.emit('game_over', gameOverPayload);
  }
}

function hasShieldAffix(e: Enemy): boolean {
  for (let i = 0; i < e.affix_count; i++) {
    if (e.affixes[i] === 'affix.shielded') return true;
  }
  return false;
}

/** Re-exported so weapon code has a single import site for the contact-knockback pair (§03.7). */
export { applyKnockbackToPlayer };
